import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import {
  DocumentsService,
  DocumentsPaginationPayload,
  DocumentItem,
} from "../../api/documents";
import { useNavigate } from "react-router-dom";

const defaultPayload: DocumentsPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "createdOn",
  direction: "desc",
  whereClause: [],
};

const DocumentsList: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DocumentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [payload, setPayload] =
    useState<DocumentsPaginationPayload>(defaultPayload);
  const lastFetchKeyRef = useRef<string>("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedDocForResult, setSelectedDocForResult] = useState<DocumentItem | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const navigate = useNavigate();

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchItems = async () => {
    setLoading(true);
    try {
      const filters: { key: string; value: string; operator: string }[] = [];

      if (searchTerm.trim()) {
        filters.push({
          key: "patientName",
          value: searchTerm.trim(),
          operator: "=",
        });
      }

      const res = await DocumentsService.paginate({
        ...payload,
        whereClause: filters,
      });
      const dataArray = res?.data?.data || res?.data?.items || [];
      setItems(Array.isArray(dataArray) ? dataArray : []);
      setTotal(res?.data?.total || res?.data?.count || 0);
    } catch (err: any) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = `${payload.curPage}|${payload.perPage}|${payload.sortBy}|${payload.direction}|${searchTerm}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    searchTerm,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');
      
      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  const handleConfirmDelete = async (id: string) => {
    if (!id) {
      toast.error("Unable to delete: missing document id.");
      return;
    }
    try {
      setLoading(true);
      await DocumentsService.delete(id);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchItems();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to delete document record."
      );
    } finally {
      setLoading(false);
    }
  };

  const getPatientNameFromDoc = (doc: any) => {
    return (
      doc?.treatment?.patient?.name ||
      doc?.treatment?.patientName ||
      doc?.patient?.name ||
      doc?.patientName ||
      doc?.treatmentId ||
      "—"
    );
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  const renderFileLink = (url?: string) => {
    if (!url) return "—";
    const short =
      url.length > 40 ? `${url.slice(0, 18)}...${url.slice(-15)}` : url;
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-blue-600 hover:underline break-all"
      >
        {short}
      </a>
    );
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  View Results
                </h2>
                <button
                  className="mt-3 md:mt-0 px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  onClick={() => navigate("/documents/create")}
                >
                  + Upload Documents
                </button>
              </div>

              {/* Controls */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Total: <span className="font-medium">{total}</span>
                  </span>

                </div>
                <div className="flex items-center gap-2">
                  <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by patient name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto relative" style={{ overflowY: 'visible' }}>
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Created On
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={6}
                        >
                          No documents found
                        </td>
                      </tr>
                    ) : (
                      items.map((doc: any) => (
                        <tr
                          key={doc.id}
                          className="border-t hover:bg-gray-50 align-top"
                          style={{ position: 'relative' }}
                        >
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {getPatientNameFromDoc(doc)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(doc.createdOn)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ overflow: 'visible' }}>
                            <div 
                              className="relative" 
                              ref={(el) => {
                                const docId = doc.id || "";
                                if (openMenuId === docId) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    buttonRefs.current[doc.id || ""] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  const docId = doc.id || "";
                                  if (openMenuId === docId) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      right: window.innerWidth - rect.right,
                                    });
                                    setOpenMenuId(docId);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === (doc.id || "") && dropdownPosition && createPortal(
                                <div 
                                  className="dropdown-menu-container fixed w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    right: `${dropdownPosition.right}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-[#016683] hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setSelectedDocForResult(doc);
                                      setShowResultModal(true);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    View Result
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setSelectedId(doc.id || "");
                                      setShowConfirm(true);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <hr />
              {/* Pagination */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Document"
                message="Are you sure you want to delete this document record?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleConfirmDelete(selectedId)}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
                variant="danger"
              />
              <SuccessModal
                isOpen={showSuccess}
                title="Document Deleted"
                message="The document record has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />

              {/* Result Preview Modal */}
              {showResultModal && selectedDocForResult && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        View Treatment Result
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowResultModal(false)}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {/* Before image */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Attach before treatment image
                      </p>
                      <div className="border-2 border-[#00A0D2]/40 rounded-2xl p-4 flex items-center justify-center bg-[#F7FCFF] min-h-[140px]">
                        {selectedDocForResult.beforeImage ? (
                          <img
                            src={selectedDocForResult.beforeImage}
                            alt="Before treatment"
                            className="max-h-40 w-auto object-contain rounded-lg shadow-sm"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            No before image uploaded
                          </span>
                        )}
                      </div>
                    </div>

                    {/* After image */}
                    <div className="mb-6">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Attach after treatment image
                      </p>
                      <div className="border-2 border-[#00A0D2]/40 rounded-2xl p-4 flex items-center justify-center bg-[#F7FCFF] min-h-[140px]">
                        {selectedDocForResult.afterImage ? (
                          <img
                            src={selectedDocForResult.afterImage}
                            alt="After treatment"
                            className="max-h-40 w-auto object-contain rounded-lg shadow-sm"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            No after image uploaded
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowResultModal(false)}
                      className="w-full py-2.5 rounded-full bg-[#005b7f] text-white text-sm font-semibold hover:opacity-90 transition"
                    >
                      Close
                    </button>
                  </motion.div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default DocumentsList;


